/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { callMiddlewares } from '../../utils/middleware-helpers';
import { MiddlewareSteps } from './enums';
import { RerunConfig } from './configs';
import { isErrorThatNeedToBeShown } from '../../utils';

import type {
    DetailedStrategies,
    MiddlewareArgs,
    MiddlewareContext,
    MiddlewareRequest,
    MiddlewareResponse,
    MiddlewareRunParams,
    MiddlewareServiceFunction,
    MiddlewareServiceParams,
    MiddlewareStrategyConfigsContainer,
    MiddlewareStrategyContainer,
} from './interfaces';

export class MiddlewareService<Ctx = MiddlewareContext> {

    protected readonly context: Ctx;
    protected readonly strategies: MiddlewareStrategyContainer<Ctx, any, any> = {};
    protected readonly strategyConfigs: MiddlewareStrategyConfigsContainer = {};

    protected readonly responseProp?: string;
    protected readonly errorProp?: string;

    constructor(params: MiddlewareServiceParams<Ctx, any, any>) {
        const {
            context = {} as Ctx,
            strategies,
            strategyConfigs = {},
            responseProp,
            errorProp,
        } = params;

        this.strategies = strategies;
        this.strategyConfigs = strategyConfigs;
        this.context = context;
        this.responseProp = responseProp;
        this.errorProp = errorProp;
    }

    run<Req = MiddlewareRequest, Res = (MiddlewareResponse | Error), Success = void, Fail = Error>(
        initialData: Req,
        params: MiddlewareRunParams = {}
    ): Promise<Success | Fail> {
        const req: Req = {
            ...initialData,
            rerunConfig: new RerunConfig({ limit: params.rerunLimit }),
        };
        const res: Res = {} as Res;

        return this.runAllSteps<Req, Res, Success, Fail>(req, res, params);
    }

    protected runAllSteps<Req, Res, Success, Fail>(req: Req, res: Res, params: MiddlewareRunParams) {
        return this.prepareRequestStep<Req, Res>(req, res, params)
            .then(() => this.sendRequestStep<Req, Res>(req, res, params))
            .then(() => this.handleResponseStep<Req, Res, Success>(req, res, params))
            .catch((err: Res) => {
                const { rerunConfig } = req as MiddlewareRequest;

                if (rerunConfig.getStep() && rerunConfig.canRerun()) {
                    rerunConfig.incrementCounter();

                    return this.runAllSteps<Req, Res, Success, Fail>(req, res, params);
                }

                return this.handleErrorsStep<Req, Res, Fail>(req, err, params);
            })
            .catch((errResult) => {
                const { rerunConfig } = req as MiddlewareRequest;

                if (rerunConfig.getStep() && rerunConfig.canRerun()) {
                    rerunConfig.incrementCounter();

                    return this.runAllSteps<Req, Res, Success, Fail>(req, res, params);
                }

                if (isErrorThatNeedToBeShown(errResult)) {
                    console.error('Unexpected internal error:', res);
                }

                return Promise.reject(errResult);
            });
    }

    protected prepareRequestStep<Req, Res>(req: Req, res: Res, params: MiddlewareRunParams) {
        const { rerunConfig } = req as MiddlewareRequest;
        const rerunStep = rerunConfig.getStep();

        if (!rerunStep || (rerunStep && rerunStep === MiddlewareSteps.Prepare)) {
            rerunConfig.setStep(null);

            return this.runStep(MiddlewareSteps.Prepare, req, res, params);
        }

        return Promise.resolve();
    }

    protected sendRequestStep<Req, Res>(req: Req, res: Res, params: MiddlewareRunParams) {
        const { rerunConfig } = req as MiddlewareRequest;
        const rerunStep = rerunConfig.getStep();

        if (!rerunStep || (rerunStep && rerunStep === MiddlewareSteps.Run)) {
            rerunConfig.setStep(null);

            return this.runStep(MiddlewareSteps.Run, req, res, params);
        }

        return Promise.resolve();
    }

    protected handleResponseStep<Req, Res, Success>(req: Req, res: Res, params: MiddlewareRunParams): Promise<Success> {
        return this.runStep(MiddlewareSteps.HandleResponse, req, res, params)
            .then(() => (this.responseProp ? res[this.responseProp] : res) as Success);
    }

    protected handleErrorsStep<Req, Res, Fail>(req: Req, res: Res, params: MiddlewareRunParams): Promise<Fail> {
        return this.runStep(MiddlewareSteps.HandleErrors, req, res, params)
            .catch(() => Promise.reject((this.errorProp ? res[this.errorProp] : res) as Fail))
            .then(() => Promise.reject((this.errorProp ? res[this.errorProp] : res) as Fail));
    }

    protected runStep<Req, Res>(step: MiddlewareSteps, req: Req, res: Res, params: MiddlewareRunParams) {
        const currentStrategy = this.getStrategyForStep(step, params.strategy);
        const currentStrategyConfig = this.getStrategyConfigForStep(step, currentStrategy, params.configs);
        const stepMiddlewares: MiddlewareServiceFunction<Ctx, Req, Res>[] | null = this.strategies[currentStrategy]?.[step] ?? null;
        const context: Ctx = {
            ...this.context,
            middlewareConfigs: currentStrategyConfig,
        };

        if (stepMiddlewares?.length > 0) {
            return callMiddlewares<MiddlewareArgs<Ctx, Req, Res>, Res>(stepMiddlewares, context, req, res);
        } else if (!this.strategies[currentStrategy]) {
            console.error(`Unknown strategy: "${currentStrategy}"`);
        } else {
            console.log(`No middlewares for strategy "${currentStrategy}" and step ${step}. If it is correct, skip that warning.`);
        }

        return Promise.resolve();
    }

    protected getStrategyForStep(step: MiddlewareSteps, strategy: string | DetailedStrategies): string {
        if (typeof strategy === 'string') {
            return strategy;
        } else if (strategy && typeof strategy[step] === 'string') {
            return strategy[step];
        }

        const existedStrategies = Object.keys(this.strategies);

        if (existedStrategies.length > 0) {
            return existedStrategies[0];
        }

        throw Error('Wrong strategy format in MiddlewareRunParams');
    }

    protected getStrategyConfigForStep(
        step: MiddlewareSteps,
        strategy: string,
        configs: MiddlewareStrategyConfigsContainer = {}
    ): Record<string, any> {
        let result = {};

        if (this.strategyConfigs[strategy] && this.strategyConfigs[strategy][step] !== undefined) {
            result = {
                ...result,
                ...this.strategyConfigs[strategy][step],
            };
        }

        if (this.strategyConfigs[strategy] !== undefined) {
            result = {
                ...result,
                ...this.strategyConfigs[strategy],
            };
        }

        if (configs[strategy] && configs[strategy][step] !== undefined) {
            result = {
                ...result,
                ...configs[strategy][step],
            };
        }

        if (configs[strategy] !== undefined) {
            result = {
                ...result,
                ...configs[strategy],
            };
        }

        return result;
    }

}
